import React, { useState, useEffect } from 'react';
import { string, bool } from 'prop-types';
import classNames from 'classnames/bind';
import { Row, Col, Button } from '@thd-olt-component-react/core-ui';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { useDataModel, QueryContext } from '@thd-nucleus/data-sources';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import { CarouselHeader } from '../core/CarouselHeader';
import { SupplementalTilesCarousel } from './SupplementalTilesCarousel';
import { SupplementalTiles } from './SupplementalTiles';
import { SupplementalRecsPods } from './SupplementalRecsPods';
import { supplementalRecsdataModel } from '../dataModel';
import { publish } from '../utils/publisher';
import useCxtPropsAndOptions from '../hooks/useCxtPropsAndOptions';
import styles from './SupplementalRecs.module.scss';

const cx = classNames.bind(styles);

const SupplementalRecsLoadingPlaceholder = () => {
  const { isMobile } = useCxtPropsAndOptions();

  return (
    <LoadingPlaceholder
      itemHeight={isMobile ? 197 : 290}
      itemsCount={6}
      txtPlaceholderLines={1}
    />
  );
};

const SupplementalRecs = ({
  anchorId,
  requestKey,
  schemaName,
  showLoading,
  title,
  buttonValue,
  subtitle,
  isDrawerShow
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [viewDetails, setViewDetails] = useState(false);
  const { isMobile, opts } = useCxtPropsAndOptions(anchorId, requestKey, schemaName);

  const { data, error, loading } = useDataModel('recs', opts);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const evntName = buttonValue || 'shop-the-look';
  const handleViewDetailsClick = () => {
    publish({
      componentName: 'supplemental-recs',
      eventName: 'shop-the-look-click',
      data: {
        eventName: evntName
      }
    });
    setViewDetails(true);
  };

  if (!isMounted || error || (!loading && !data?.recs)) {
    return null;
  }

  const productsData = data?.recs?.products;
  const containerTitle = title || data?.recs?.metadata?.title;
  const buttonText = buttonValue || 'Shop the Look';
  return (
    <Row id={schemaName} data-type="container" data-component={`DynamicRecs-${schemaName}`}>
      {
        showLoading && loading && !data?.recs
        && <SupplementalRecsLoadingPlaceholder />
      }
      {
        productsData?.length
        && (
          <Col className={cx('supplemental-recs')}>
            <CarouselHeader title={containerTitle} subtitle={subtitle} />
            <meta data-prop="name" content={schemaName} />
            <Col nopadding>
              {isMobile
                ? (
                  <SupplementalTiles
                    productsData={productsData}
                    schemaName={schemaName}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShow}
                  />
                )
                : (
                  <SupplementalTilesCarousel
                    productsData={productsData}
                    schemaName={schemaName}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShow}
                  />
                )}
            </Col>
            <Row>
              <Col
                fallback="6"
                xs="3"
                sm="2"
                className={cx('supplemental-recs__btn-wrapper')}
              >
                <Button
                  outline
                  onClick={handleViewDetailsClick}
                >
                  {buttonText}
                </Button>
              </Col>
            </Row>
            <SupplementalRecsPods
              productsData={productsData}
              storeId={opts.variables.storeId}
              zipCode={opts.variables.zipCode}
              schemaName={schemaName}
              isDrawerOpen={viewDetails}
              handleViewDetailsClick={() => setViewDetails(false)}
              podTitle={title}
            />
          </Col>
        )
      }
    </Row>
  );
};

SupplementalRecs.propTypes = {
  anchorId: string.isRequired,
  requestKey: string.isRequired,
  schemaName: string.isRequired,
  showLoading: bool,
  title: string,
  buttonValue: string,
  subtitle: string,
  isDrawerShow: bool,
};

SupplementalRecs.defaultProps = {
  showLoading: true,
  title: null,
  buttonValue: null,
  subtitle: null,
  isDrawerShow: false
};

SupplementalRecs.dataModel = supplementalRecsdataModel;
SupplementalRecs.displayName = 'SupplementalRecs';

const HydratedSupplementalRecs = withHydrator(
  {
    delay: 1500,
    placeholder: (<SupplementalRecsLoadingPlaceholder />),
    preserveCtxVal: 'clientStore',
    contexts: declareContexts([QueryContext])
  },
  SupplementalRecs
);

const SupplementalRecommendations = withErrorBoundary(HydratedSupplementalRecs);
SupplementalRecommendations.dataModel = supplementalRecsdataModel;

export { SupplementalRecommendations };
